import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  useDeleteInventory,
  useInventoryById,
  usePurchaseOrdersByInventoryId,
  useSalesOrdersByInventoryId,
  useUpdateInventory,
} from "@/hooks";
import { useUserStore } from "@/stores/user";
import { formatDateOnly } from "@/utils/formatting";
import { getPurchaseOrderTotal, getSalesOrderTotal } from "@/utils/order-total";
import { Loader2 } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import {
  buildInventoryWorkspacePath,
  formatCurrency,
  formatNumber,
  formatStatusLabel,
  getInventoryBookingBlockDetails,
  getInventoryOperationalStatus,
} from "./operations-shared";

interface InventorySheetProps {
  inventoryId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function InventorySheet(props: Readonly<InventorySheetProps>) {
  const { inventoryId, open, onOpenChange } = props;
  const { hasFullAccess } = useUserStore();
  const inventoryQuery = useInventoryById(inventoryId);
  const purchaseOrdersQuery = usePurchaseOrdersByInventoryId(inventoryId);
  const salesOrdersQuery = useSalesOrdersByInventoryId(inventoryId);
  const deleteInventoryMutation = useDeleteInventory();
  const updateInventoryMutation = useUpdateInventory();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isArchiveDialogOpen, setIsArchiveDialogOpen] = useState(false);
  const isDeleting = deleteInventoryMutation.isPending;
  const isArchiving = updateInventoryMutation.isPending;
  const inventory = inventoryQuery.data;
  const isArchived = inventory?.status === "archived";
  const inventoryBookingBlock = inventory
    ? getInventoryBookingBlockDetails({ inventory })
    : null;
  const purchaseOrders = purchaseOrdersQuery.data || [];
  const salesOrders = salesOrdersQuery.data || [];

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full max-w-full overflow-y-auto p-0 md:max-w-2xl"
      >
        <div className="border-b px-6 py-5">
          <SheetHeader className="text-left">
            <SheetTitle>Inventory Details</SheetTitle>
            <SheetDescription>
              Review the inventory summary, linked orders, and embedded account
              state.
            </SheetDescription>
          </SheetHeader>
        </div>

        {!inventory ? (
          <div className="px-6 py-6 text-sm text-muted-foreground">
            {inventoryQuery.isLoading ? "Loading..." : "Inventory not found."}
          </div>
        ) : (
          <div className="space-y-6 px-6 py-6 text-sm">
            <div className="rounded-xl border p-4 space-y-3">
              <div className="text-lg font-semibold">{inventory.name}</div>
              <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                <span className="rounded-md border px-2 py-0.5 font-medium text-foreground">
                  {formatStatusLabel(
                    getInventoryOperationalStatus({ inventory }),
                  )}
                </span>
                <span>{inventory.productName}</span>
                <span>
                  Account {formatStatusLabel(inventory.accountStatus)}
                </span>
              </div>
              <div className="grid gap-2 md:grid-cols-2">
                <div>
                  Purchased:{" "}
                  {formatNumber({ value: inventory.totalPurchasedMiles })}
                </div>
                <div>
                  Sold: {formatNumber({ value: inventory.totalSoldMiles })}
                </div>
                <div>
                  Available: {formatNumber({ value: inventory.availableMiles })}
                </div>
                <div>
                  Account Balance:{" "}
                  {inventory.accountBalance == null
                    ? "-"
                    : formatNumber({ value: inventory.accountBalance })}
                </div>
                <div>
                  Variance:{" "}
                  {inventory.accountBalance == null
                    ? "-"
                    : formatNumber({
                        value:
                          inventory.accountBalance - inventory.availableMiles,
                      })}
                </div>
                <div>2FA: {formatStatusLabel(inventory.twoFaStatus)}</div>
                <div>
                  Blocked Until:{" "}
                  {inventoryBookingBlock?.tbd
                    ? "TBD"
                    : formatDateOnly(inventoryBookingBlock?.blockedUntil) || "-"}
                </div>
                <div>Owner: {inventory.accountOwnerName || "-"}</div>
                <div>Email: {inventory.accountEmail || "-"}</div>
                {inventory.username ? (
                  <div>Username: {inventory.username}</div>
                ) : null}
                <div>POs: {purchaseOrders.length}</div>
                <div>SOs: {salesOrders.length}</div>
                {inventory.expirationInfo ? (
                  <div className="md:col-span-2">
                    Expiration: {inventory.expirationInfo}
                  </div>
                ) : null}
              </div>
              <div className="flex flex-wrap gap-2">
                <Button asChild size="sm">
                  <Link
                    to={buildInventoryWorkspacePath({
                      inventoryId: inventory.id,
                    })}
                    onClick={() => onOpenChange(false)}
                  >
                    Open Inventory Workspace
                  </Link>
                </Button>
                {hasFullAccess() && isArchived && (
                  <Button
                    variant="outline"
                    size="sm"
                    loading={updateInventoryMutation.isPending}
                    loadingText="Unarchiving..."
                    onClick={() =>
                      updateInventoryMutation.mutate({
                        id: inventory.id,
                        data: {
                          status: inventory.archivedFromStatus ?? "draft",
                          archivedFromStatus: undefined,
                        },
                      })
                    }
                  >
                    Unarchive
                  </Button>
                )}
                {hasFullAccess() && !isArchived && (
                  <AlertDialog
                    open={isArchiveDialogOpen}
                    onOpenChange={(nextOpen) => {
                      if (isArchiving) return;
                      setIsArchiveDialogOpen(nextOpen);
                    }}
                  >
                    <AlertDialogTrigger asChild>
                      <Button variant="outline" size="sm">
                        Archive
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Archive inventory?</AlertDialogTitle>
                        <AlertDialogDescription>
                          <strong>{inventory.name}</strong> will be hidden from the default list and all actions will be disabled. You can unarchive it at any time.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel disabled={isArchiving}>
                          Cancel
                        </AlertDialogCancel>
                        <AlertDialogAction
                          disabled={isArchiving}
                          onClick={(event) => {
                            event.preventDefault();
                            updateInventoryMutation.mutate(
                              {
                                id: inventory.id,
                                data: {
                                  status: "archived",
                                  archivedFromStatus: inventory.status,
                                },
                              },
                              {
                                onSuccess: () => setIsArchiveDialogOpen(false),
                                onError: () => setIsArchiveDialogOpen(false),
                              },
                            );
                          }}
                        >
                          {isArchiving ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              Archiving...
                            </>
                          ) : (
                            "Archive"
                          )}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
                {hasFullAccess() && (
                  <AlertDialog
                    open={isDeleteDialogOpen}
                    onOpenChange={(nextOpen) => {
                      if (isDeleting) return;
                      setIsDeleteDialogOpen(nextOpen);
                    }}
                  >
                    <AlertDialogTrigger asChild>
                      <Button variant="destructive" size="sm">
                        Delete Inventory
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete inventory?</AlertDialogTitle>
                        <AlertDialogDescription>
                          This will permanently delete <strong>{inventory.name}</strong> and all linked purchase orders, sell orders, invoices, and payments. This action cannot be undone.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel disabled={isDeleting}>
                          Cancel
                        </AlertDialogCancel>
                        <AlertDialogAction
                          className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                          disabled={isDeleting}
                          onClick={(event) => {
                            event.preventDefault();
                            deleteInventoryMutation.mutate(
                              { inventoryId: inventory.id },
                              {
                                onSuccess: () => {
                                  setIsDeleteDialogOpen(false);
                                  onOpenChange(false);
                                },
                                onError: () => {
                                  setIsDeleteDialogOpen(false);
                                },
                              },
                            );
                          }}
                        >
                          {isDeleting ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              Deleting...
                            </>
                          ) : (
                            "Delete"
                          )}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
              </div>
            </div>

            <div className="rounded-xl border p-4 space-y-3">
              <div className="font-medium">Purchase Orders</div>
              {purchaseOrders.length === 0 ? (
                <div className="text-muted-foreground">
                  No purchase orders linked yet.
                </div>
              ) : (
                purchaseOrders.slice(0, 5).map((purchaseOrder) => (
                  <div
                    key={purchaseOrder.id}
                    className="flex items-center justify-between gap-3 rounded-lg border p-3"
                  >
                    <div>
                      <div className="font-medium">
                        {purchaseOrder.productName}
                      </div>
                      <div className="text-muted-foreground">
                        {purchaseOrder.vendorDisplayName ||
                          purchaseOrder.vendorEmail ||
                          "Unknown vendor"}
                      </div>
                    </div>
                    <div className="text-right">
                      <div>
                        {formatCurrency({
                          value: getPurchaseOrderTotal({
                            buyPrice: purchaseOrder.buyPrice,
                            taxCost: purchaseOrder.taxCost,
                            totalAmount: purchaseOrder.totalAmount,
                          }),
                        })}
                      </div>
                      <div className="text-muted-foreground">
                        {formatStatusLabel(purchaseOrder.paymentStatus)}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="rounded-xl border p-4 space-y-3">
              <div className="font-medium">Sell Orders</div>
              {salesOrders.length === 0 ? (
                <div className="text-muted-foreground">
                  No sell orders linked yet.
                </div>
              ) : (
                salesOrders.slice(0, 5).map((salesOrder) => (
                  <div
                    key={salesOrder.id}
                    className="flex items-center justify-between gap-3 rounded-lg border p-3"
                  >
                    <div>
                      <div className="font-medium">
                        {salesOrder.productName}
                      </div>
                      <div className="text-muted-foreground">
                        {salesOrder.clientDisplayName ||
                          salesOrder.clientEmail ||
                          "Unknown client"}
                      </div>
                    </div>
                    <div className="text-right">
                      <div>
                        {formatCurrency({
                          value: getSalesOrderTotal({
                            revenue: salesOrder.revenue,
                            taxCost: salesOrder.taxCost,
                            totalAmount: salesOrder.totalAmount,
                          }),
                        })}
                      </div>
                      <div className="text-muted-foreground">
                        {formatStatusLabel(salesOrder.status)}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
